/*****************************************************************
 *  CUSTOMER BUTLER - EVENTS THE DOOR REFUSED (CRM Setup)          *
 *  File: apps/web/src/pages/customer/DoorRefusals.js              *
 *  What it does: one card on CRM Setup, for Customer Butler       *
 *  managers, that lists every call the timeline door turned away  *
 *  (which event, which sender, why, when, who), and beside it how *
 *  many events are waiting in the holding list, with the way to   *
 *  the existing "Attach to a customer" flow.                      *
 *  Reads: GET /customer/timeline/refused and the holding list.    *
 *  Writes: nothing. Nothing on this card can change or remove a   *
 *  row (the server keeps them forever).                           *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 - THE RULING AND THE FIXED WORDS                     *
 *  SECTION 2 - LOADING THE TWO LISTS                              *
 *  SECTION 3 - ONE REFUSED CALL                                   *
 *  SECTION 4 - THE HOLDING LINE (the way to Attach)               *
 *  SECTION 5 - THE CARD                                           *
 *****************************************************************/

import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from './customerIcons';
import { QuietLine, Notice, StatusChip } from './customerParts';
import { formatMoment } from './customerDates';
import { useCustomerAccessView } from './customerAccessView';
import { useButlerLook } from './customerLook';
import { fetchDoorRefusals, fetchHoldingEntries } from './customerApi';
import { HOLDING_CARD_ID, openTheHoldingCard } from './SuiteTimeline';
import './customer.css';

/*****************************************************************
 *  SECTION 1 - THE RULING AND THE FIXED WORDS                     *
 *  WHY (2026-10-15, Fable/James, BACKLOG 317): an owner needs to  *
 *  see the events that never made it onto a customer's timeline.  *
 *  The senders' own outboxes are files inside each sender (THE    *
 *  WALL), so this card shows what the DOOR knows: the calls it    *
 *  refused (written at the door, append-only) and the events it   *
 *  took but could not match (the holding list). Honest UI: it     *
 *  says plainly what it cannot see.                               *
 *****************************************************************/

export const REFUSED_CARD_ID = 'cb-refused-card'; // the card's address on the page

// What the card says when there is nothing to show.
const NOTHING_REFUSED_WORDS = 'The door has refused nothing so far.';

// What the card cannot see, said once, plainly.
const OUTBOX_WORDS = 'An event a Butler could not deliver at all (for example while this '
  + 'server was restarting) waits in that Butler\'s own outbox and is sent again by it. '
  + 'Those are not listed here until the door hears them.';

// Said to managers who are not the platform owner.
const OWNER_ONLY_WORDS = 'Calls that never proved who sent them name no company, so only '
  + 'the owner of the whole system sees them.';

/*****************************************************************
 *  SECTION 2 - LOADING THE TWO LISTS                              *
 *****************************************************************/

/* ---- useRefusedAndWaiting(isAllowed) -----------------------------
   In: true when this person may see CRM Setup.
   Out: { refusedAnswer (null while loading, or { refusals,
   showsUnprovenCalls, countedNotWrittenYet }), waitingCount (null
   while loading), couldNotLoad, reload }.                       */
function useRefusedAndWaiting(isAllowed) {
  const [refusedAnswer, setRefusedAnswer] = useState(null);
  const [waitingCount, setWaitingCount] = useState(null);
  const [couldNotLoad, setCouldNotLoad] = useState(false);

  const reload = useCallback(async () => {
    try {
      const loadedRefusals = await fetchDoorRefusals();
      const loadedWaiting = await fetchHoldingEntries();

      setRefusedAnswer(loadedRefusals || { refusals: [] });
      setWaitingCount(Array.isArray(loadedWaiting) ? loadedWaiting.length : 0);
      setCouldNotLoad(false);
    } catch (loadProblem) {
      setRefusedAnswer({ refusals: [] });
      setWaitingCount(0);
      setCouldNotLoad(true);
    }
  }, []);

  useEffect(() => {
    if (isAllowed) {
      reload();
    }
  }, [isAllowed, reload]);

  return { refusedAnswer, waitingCount, couldNotLoad, reload };
}

/*****************************************************************
 *  SECTION 3 - ONE REFUSED CALL                                   *
 *****************************************************************/

/* ---- describeSender(refusal) -- the sender chip's look ---------
   A proven sender gets the shield and its name; a call that
   proved nothing says so (word + icon, never colour alone).     */
function describeSender(refusal) {
  const senderName = refusal.senderWords || refusal.senderNamed || 'No sender named';

  if (refusal.senderWasProven) {
    return { tone: 'neutral', icon: 'shield', word: `From ${senderName} (signed)` };
  }

  return { tone: 'bad', icon: 'warn', word: `Not proven: ${senderName}` };
}

/* ---- describeRefusalMeta(refusal) -- when, who, answer, from where ---- */
function describeRefusalMeta(refusal) {
  const answerWords = refusal.statusCode ? `answered ${refusal.statusCode}` : '';
  const countedWords = refusal.timesCounted > 1 ? `${refusal.timesCounted} calls counted together` : '';

  return [formatMoment(refusal.refusedAt), refusal.whoName, answerWords, refusal.callerAddress, countedWords]
    .filter(Boolean)
    .join(', ');
}

/* ---- RefusedCallRow({ refusal }) -- one refused call ---- */
function RefusedCallRow({ refusal }) {
  const senderLook = describeSender(refusal);
  const eventWords = refusal.eventName || 'No event name';

  return (
    <li className="cb-item cb-refused-row">
      <div className="cb-item-top">
        <span className="cb-item-name">{eventWords}</span>
        <StatusChip tone={senderLook.tone} icon={senderLook.icon} word={senderLook.word} />
      </div>
      <div className="cb-item-body">{`Why: ${refusal.refusalWords}`}</div>
      {refusal.summary ? <div className="cb-item-meta">{`It said: ${refusal.summary}`}</div> : null}
      <div className="cb-item-meta">{describeRefusalMeta(refusal)}</div>
    </li>
  );
}

/*****************************************************************
 *  SECTION 4 - THE HOLDING LINE (the way to Attach)               *
 *****************************************************************/

/* ---- HoldingLine({ waitingCount }) -------------------------------
   How many events the door took but matched to no customer, and a
   button that opens the existing waiting list (where each one has
   "Attach to a customer").                                       */
function HoldingLine({ waitingCount }) {
  if (waitingCount === null) {
    return <QuietLine icon="clock">Counting the waiting list</QuietLine>;
  }

  const isNothingWaiting = waitingCount === 0;
  const waitingWords = waitingCount === 1
    ? '1 event reached the door but matched no customer.'
    : `${waitingCount} events reached the door but matched no customer.`;

  if (isNothingWaiting) {
    return <QuietLine icon="check">Nothing is waiting to be matched.</QuietLine>;
  }

  return (
    <div className="cb-refused-holding">
      <StatusChip tone="warn" icon="clock" word={`${waitingCount} waiting`} />
      <span className="cb-refused-holding-words">{waitingWords}</span>
      <a
        className="bc-btn is-ghost bc-btn-auto"
        href={`#${HOLDING_CARD_ID}`}
        onClick={(clickEvent) => {
          clickEvent.preventDefault();
          openTheHoldingCard();
        }}
      >
        <Icon name="link" size="button" /> Open the waiting list to attach them
      </a>
    </div>
  );
}

/*****************************************************************
 *  SECTION 5 - THE CARD                                           *
 *  Shown only to people whose level may change setup; the server  *
 *  refuses everyone else (Honest UI).                             *
 *****************************************************************/

/* ---- DoorRefusalsCard() -- the whole card ---- */
export function DoorRefusalsCard() {
  useButlerLook();

  const accessView = useCustomerAccessView(); // what this person may be shown
  const { refusedAnswer, waitingCount, couldNotLoad } = useRefusedAndWaiting(accessView.mayChangeSetup);

  if (!accessView.mayChangeSetup) {
    return null;
  }

  const refusals = (refusedAnswer && refusedAnswer.refusals) || [];
  const isLoading = refusedAnswer === null;
  const countedNotWrittenYet = (refusedAnswer && refusedAnswer.countedNotWrittenYet) || 0;
  const showsUnprovenCalls = Boolean(refusedAnswer && refusedAnswer.showsUnprovenCalls);

  return (
    <section className="cb-page cb-setup" id={REFUSED_CARD_ID}>
      <details className="cb-section">
        <summary>
          <Icon name="ban" /> {isLoading ? 'Events the door refused' : `Events the door refused (${refusals.length})`}
        </summary>
        <div className="cb-setup-body">
          <p className="cb-sub">
            Calls another Butler made to Customer Butler&apos;s timeline door that the door turned away,
            newest first. Each one is kept with when, who and why. Nothing here is ever thrown away.
          </p>
          <HoldingLine waitingCount={waitingCount} />
          {couldNotLoad ? <Notice tone="bad" icon="warn">The refused calls could not be loaded. Nothing was changed.</Notice> : null}
          {isLoading ? <QuietLine icon="clock">Loading</QuietLine> : null}
          {!isLoading && refusals.length === 0 && !couldNotLoad ? <QuietLine icon="check">{NOTHING_REFUSED_WORDS}</QuietLine> : null}
          {countedNotWrittenYet > 0 ? (
            <Notice tone="warn" icon="warn">
              {`${countedNotWrittenYet} more calls that proved nothing were refused this hour. They are being counted, and the count will be written as one row.`}
            </Notice>
          ) : null}
          <ul className="cb-list-rows">
            {refusals.map((oneRefusal) => <RefusedCallRow key={oneRefusal.id} refusal={oneRefusal} />)}
          </ul>
          <p className="cb-item-meta">{OUTBOX_WORDS}</p>
          {!isLoading && !showsUnprovenCalls ? <p className="cb-item-meta">{OWNER_ONLY_WORDS}</p> : null}
        </div>
      </details>
    </section>
  );
}

export default DoorRefusalsCard;
